import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, GripVertical, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ImageStoryPanelImageSlot } from '@/components/narration/ImageStoryPanelImageSlot';
import { ImageStoryPanelRetryButton } from '@/components/narration/ImageStoryPanelRetryButton';
import { NarrationSceneAudioAttach } from '@/components/narration/NarrationSceneAudioAttach';
import { cropBoxCacheKey, sceneImageUrlWithCropRevision } from '@/lib/imageStoryCrop';
import { imageStoryPanelImageStateFromScene } from '@/lib/imageStoryPanelImageState';
import { narrationTextForScene, sceneSkipsNarration } from '@/lib/narrationSceneScripts';

function sceneRowKey(scene, index) {
  return scene?.client_id || `row-${scene?.image_url || index}-${scene?.segment_number || index}`;
}

/**
 * Original sortable row list — drag grip, up/down arrows, expandable script blocks.
 */
export function NarrationSceneClassicList({
  scenes = [],
  onReorder,
  onRemove,
  onSelectScene,
  onToggleSkipNarration,
  onSilentDurationChange,
  onRetryImage,
  onSceneAudioChange,
  imageRetryBusy = null,
  disabled = false,
  className = '',
}) {
  const rowRefs = useRef([]);
  const dragRef = useRef(null);
  const [dragFromIndex, setDragFromIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resolveDropIndex = useCallback((clientY) => {
    const rows = rowRefs.current.filter(Boolean);
    if (!rows.length) return 0;
    for (let i = 0; i < rows.length; i += 1) {
      const rect = rows[i].getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      if (clientY < mid) return i;
    }
    return rows.length - 1;
  }, []);

  const endDrag = useCallback(
    (clientY) => {
      const state = dragRef.current;
      if (!state || state.fromIndex == null) return;
      dragRef.current = null;
      setDragFromIndex(null);
      setDragOverIndex(null);
      if (clientY == null) return;
      const to = resolveDropIndex(clientY);
      if (to !== state.fromIndex) onReorder(state.fromIndex, to);
    },
    [onReorder, resolveDropIndex],
  );

  useEffect(() => {
    if (dragFromIndex == null) return undefined;

    const onPointerMove = (event) => {
      const over = resolveDropIndex(event.clientY);
      dragRef.current = { ...dragRef.current, overIndex: over };
      setDragOverIndex(over);
    };

    const onPointerUp = (event) => {
      endDrag(event.clientY);
    };

    const onPointerCancel = () => {
      endDrag(null);
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
  }, [dragFromIndex, endDrag, resolveDropIndex]);

  const onGripPointerDown = (index, event) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    const target = event.currentTarget;
    if (target.setPointerCapture) {
      try {
        target.setPointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
    }
    dragRef.current = { fromIndex: index, overIndex: index, pointerId: event.pointerId };
    setDragFromIndex(index);
    setDragOverIndex(index);
  };

  const moveByButton = (index, delta) => {
    if (disabled) return;
    const to = index + delta;
    if (to < 0 || to >= scenes.length) return;
    onReorder(index, to);
  };

  if (!scenes.length) return null;

  return (
    <div className={className} data-testid="narration-scene-classic-list">
      <p className="text-[11px] narration-wizard-hint leading-relaxed mb-2">
        Drag the grip to reorder, or use the arrows. Click a scene row to expand the full script.
      </p>
      <div className="scene-list-resizable-panel">
      <div className="narration-scene-reorder-list overflow-hidden divide-y divide-[var(--glass-border)]">
        {scenes.map((scene, index) => {
          const rowId = sceneRowKey(scene, index);
          const expanded = expandedIds.has(rowId);
          const isDragging = dragFromIndex === index;
          const isDropTarget =
            dragOverIndex === index && dragFromIndex != null && dragFromIndex !== index;
          const narration = sceneSkipsNarration(scene)
            ? 'Visual only — no narrator voice'
            : (narrationTextForScene(scene) || 'Add narration via Update script');
          const sceneDescription = String(scene.scene_text || scene.text || '').trim();
          const panelImage = imageStoryPanelImageStateFromScene(scene);
          const retryBusy = imageRetryBusy === panelImage.panelNumber;
          const resolvedImageUrl = scene.image_url
            ? sceneImageUrlWithCropRevision(scene.image_url, scene)
            : null;

          return (
            <div
              key={rowId}
              ref={(el) => {
                rowRefs.current[index] = el;
              }}
              data-scene-row
              data-testid={`narration-scene-row-${scene.segment_number}`}
              className={`narration-scene-reorder-list__row flex gap-2 sm:gap-3 items-stretch transition-colors ${
                isDragging ? 'opacity-50' : ''
              } ${isDropTarget ? 'narration-scene-reorder-list__row--drop-target' : ''}`}
            >
              <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                onPointerDown={(e) => onGripPointerDown(index, e)}
                className={`narration-scene-reorder-list__grip flex shrink-0 flex-col items-center justify-center w-10 sm:w-11 border-r touch-none select-none ${
                  disabled
                    ? 'cursor-not-allowed opacity-40'
                    : 'cursor-grab active:cursor-grabbing'
                }`}
                aria-label={`Drag scene ${scene.segment_number} to reorder`}
                data-testid={`drag-scene-handle-${scene.segment_number}`}
              >
                <GripVertical className="w-4 h-4 pointer-events-none" />
              </div>

              <div className="flex shrink-0 flex-col justify-center gap-0.5 py-1">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={disabled || index === 0}
                  onClick={() => moveByButton(index, -1)}
                  aria-label={`Move scene ${scene.segment_number} up`}
                  data-testid={`move-scene-up-${scene.segment_number}`}
                >
                  <ChevronUp className="w-4 h-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  disabled={disabled || index === scenes.length - 1}
                  onClick={() => moveByButton(index, 1)}
                  aria-label={`Move scene ${scene.segment_number} down`}
                  data-testid={`move-scene-down-${scene.segment_number}`}
                >
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </div>

              <ImageStoryPanelImageSlot
                panelImage={panelImage}
                imageUrl={resolvedImageUrl}
                alt={`Scene ${scene.segment_number}`}
                busy={retryBusy}
                disabled={disabled}
                onSelect={() => onSelectScene?.(scene)}
                onRetry={() => onRetryImage?.(scene)}
                testId={`view-image-story-scene-${scene.segment_number}`}
              />

              <div className="flex-1 min-w-0 py-3 pr-1">
                <button
                  type="button"
                  className="w-full text-left"
                  onClick={() => toggleExpanded(rowId)}
                  aria-expanded={expanded}
                >
                  <p className="text-xs narration-wizard-subheading mb-1 flex items-center gap-1 flex-wrap">
                    <span>Scene {scene.segment_number}</span>
                    {!panelImage.hasImage && panelImage.canRetry ? (
                      <span className="text-[10px] font-normal narration-wizard-hint">
                        · {panelImage.missingImageLabel}
                      </span>
                    ) : null}
                    {!panelImage.hasImage && panelImage.isActive ? (
                      <span className="text-[10px] font-normal narration-wizard-hint">
                        · {panelImage.statusLabel}
                      </span>
                    ) : null}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`}
                    />
                  </p>
                  {!expanded ? (
                    <p className="text-xs narration-wizard-hint leading-relaxed line-clamp-3">
                      <span className="font-semibold narration-wizard-subheading">Narration: </span>
                      {narration}
                    </p>
                  ) : null}
                  {scene.audio_url && !expanded ? (
                    <p className="text-[10px] narration-wizard-hint mt-1 flex items-center gap-1">
                      <span className="font-semibold narration-wizard-subheading">Audio:</span>
                      attached
                      {Number(scene.audio_duration || scene.duration) > 0
                        ? ` (${Number(scene.audio_duration || scene.duration).toFixed(1)}s)`
                        : ''}
                    </p>
                  ) : null}
                </button>

                {expanded ? (
                  <div className="mt-2 space-y-2 text-xs">
                    <div>
                      <p className="font-semibold narration-wizard-subheading mb-1">Narration script</p>
                      <p className="narration-wizard-hint leading-relaxed whitespace-pre-wrap">{narration}</p>
                    </div>
                    {sceneDescription ? (
                      <div>
                        <p className="font-semibold narration-wizard-subheading mb-1">Scene description</p>
                        <p className="narration-wizard-hint leading-relaxed whitespace-pre-wrap">{sceneDescription}</p>
                      </div>
                    ) : null}
                    {panelImage.error ? (
                      <p className="text-[11px] text-red-400 leading-relaxed">{panelImage.error}</p>
                    ) : null}
                    <ImageStoryPanelRetryButton
                      panelImage={panelImage}
                      busy={retryBusy}
                      variant="inline"
                      className="mt-2"
                      onClick={() => onRetryImage?.(scene)}
                    />
                    <NarrationSceneAudioAttach
                      scene={scene}
                      disabled={disabled}
                      onAudioChange={onSceneAudioChange}
                      className="mt-2"
                    />
                  </div>
                ) : (
                  <NarrationSceneAudioAttach
                    scene={scene}
                    disabled={disabled}
                    onAudioChange={onSceneAudioChange}
                    className="mt-2"
                  />
                )}

                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                  <div className="flex items-center gap-2">
                    <Switch
                      id={`skip-narration-${scene.client_id || scene.segment_number}`}
                      checked={sceneSkipsNarration(scene)}
                      disabled={disabled}
                      onCheckedChange={(checked) =>
                        onToggleSkipNarration?.(
                          scene.segment_number,
                          checked,
                          scene.client_id,
                        )
                      }
                      data-testid={`skip-narration-scene-${scene.segment_number}`}
                    />
                    <Label
                      htmlFor={`skip-narration-${scene.client_id || scene.segment_number}`}
                      className="text-[11px] narration-wizard-hint cursor-pointer"
                    >
                      No narration
                    </Label>
                  </div>
                  {sceneSkipsNarration(scene) ? (
                    <div className="flex items-center gap-1.5">
                      <Label
                        htmlFor={`silent-duration-${scene.client_id || scene.segment_number}`}
                        className="text-[10px] narration-wizard-hint whitespace-nowrap"
                      >
                        Hold (sec)
                      </Label>
                      <Input
                        id={`silent-duration-${scene.client_id || scene.segment_number}`}
                        type="number"
                        min={1}
                        max={60}
                        step={0.5}
                        value={Number(scene.duration) || 3}
                        disabled={disabled}
                        onChange={(e) =>
                          onSilentDurationChange?.(
                            scene.segment_number,
                            e.target.value,
                            scene.client_id,
                          )
                        }
                        className="h-7 w-16 px-2 text-xs rounded-lg"
                        data-testid={`silent-duration-scene-${scene.segment_number}`}
                      />
                    </div>
                  ) : null}
                </div>
              </div>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 self-start m-2 h-8 w-8 narration-wizard-hint hover:bg-red-500/10 hover:text-red-400"
                disabled={disabled}
                onClick={() => onRemove?.(scene.segment_number)}
                aria-label={`Remove scene ${scene.segment_number}`}
                data-testid={`remove-image-story-scene-${scene.segment_number}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
      </div>
      </div>
    </div>
  );
}
